// Token Types
export const TokenStatus = {
  Active: 'Active',
  RefreshFailed: 'RefreshFailed',
  RequiresReauth: 'RequiresReauth',
  Revoked: 'Revoked'
} as const;

export type TokenStatus = typeof TokenStatus[keyof typeof TokenStatus];

// Allowed provider types (must match backend enum IoLabs.Api.TokenServer.Data.Enums.ProviderType)
export const ProviderTypes = [
  'Google',
  'Microsoft',
  'Trimble',
  'Autodesk',
  'BuildAgil',
  'Custom'
] as const;

export type ProviderType = typeof ProviderTypes[number] | 'Unknown';

export const GrantType = {
  AuthorizationCode: 'authorization_code',
  RefreshToken: 'refresh_token',
  ClientCredentials: 'client_credentials'
} as const;

export type GrantType = typeof GrantType[keyof typeof GrantType];

// Token Interfaces
export interface Token {
  id: string;
  tokenCode?: string;
  providerId: string;
  providerCode?: string;
  providerTemplateCode?: string;
  providerName: string;
  providerType: ProviderType;
  templateName?: string;
  providerIsActive?: boolean;
  providerTemplateIsActive?: boolean;
  expiresAt: string;
  expiresInSeconds: number;
  status: TokenStatus;
  scope: string;
  lastRefreshAt?: string;
  createdAt: string;
  // External user information from OAuth provider
  externalUserId?: string;
  externalUserEmail?: string;
  externalUserName?: string;
  externalUserFamilyName?: string;
}

export interface TokenDetails extends Token {
  accessToken: string;
  refreshToken?: string;
  tokenType: string;
}

export interface CreateTokenRequest {
  providerId: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  tokenType: string;
  scope: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  newExpiresAt: string;
  message: string;
}

export interface UpdateTokenCodesRequest {
  tokenCode: string;
  providerCode: string;
  templateCode: string;
}

// Provider Interfaces
export interface IdentityProvider {
  id: string;
  templateId?: string | null;
  templateName?: string;
   templateIsActive?: boolean;
  name: string;
  clientId: string;
  providerType: ProviderType;
  isCustomProvider: boolean;
  isActive: boolean;
  createdAt: string;
  callbackUrl?: string;
  // Effective values (computed from template + overrides)
  authorizationUrl?: string;
  tokenUrl?: string;
  userInfoUrl?: string;
  revocationUrl?: string;
  scopes?: string[] | string;
  requiresPkce?: boolean;
  tokenEndpointAuthMethod?: string;
}

export interface CreateProviderRequest {
  templateId?: string | null;
  name: string;
  clientId: string;
  clientSecret: string;
  callbackUrl?: string;
  // For template-based providers (optional overrides)
  scopesOverride?: string[] | string;
  // For custom providers (required if templateId is null)
  authorizationUrl?: string;
  tokenUrl?: string;
  userInfoUrl?: string;
  revocationUrl?: string;
  scopes?: string[] | string;
  requiresPkce?: boolean;
  tokenEndpointAuthMethod?: string;
}

export interface UpdateProviderRequest {
  templateId?: string | null;
  name?: string;
  clientId?: string;
  clientSecret?: string;
  callbackUrl?: string;
  scopesOverride?: string[] | string;
  authorizationUrl?: string;
  tokenUrl?: string;
  userInfoUrl?: string;
  revocationUrl?: string;
  scopes?: string[] | string;
  requiresPkce?: boolean;
  tokenEndpointAuthMethod?: string;
  isActive?: boolean;
}

// Template Interfaces
export interface ProviderTemplate {
  id: string;
  providerType: ProviderType;
  name: string;
  authorizationUrl: string;
  tokenUrl: string;
  userInfoUrl?: string;
  revocationUrl?: string;
  defaultScopes: string[] | string;
  requiresPkce: boolean;
  supportedGrantTypes: string;
  tokenEndpointAuthMethod: string;
  isActive: boolean;
  metadata?: Record<string, any>;
}

// API Response Types
export interface ApiResponse<T> {
  data: T;
  total?: number;
}

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
  statusCode: number;
}

// User Interface
export interface User {
  id: string;
  externalUserId: string;
  email: string;
  displayName?: string;
}

// Config Types
export interface AppConfig {
  apiBaseUrl: string;
  keycloak: {
    realmUrl: string;
    clientId: string;
  };
}

// Keycloak Types
export interface KeycloakTokenParsed {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
}

// Form Types for React Hook Form
export interface ProviderFormData {
  templateId: string | null;
  name: string;
  clientId: string;
  clientSecret: string;
  callbackUrl: string;
  scopesOverride: string[];
  isCustomProvider: boolean;
  // Custom provider fields
  authorizationUrl: string;
  tokenUrl: string;
  userInfoUrl: string;
  revocationUrl: string;
  scopes: string[];
  requiresPkce: boolean;
  tokenEndpointAuthMethod: string;
}

// UI State Types
export interface TokenCardProps {
  token: Token;
  onRefresh: (tokenId: string) => void;
  onDelete: (tokenId: string) => void;
  onReauthorize: (providerId: string) => void;
  isRefreshing?: boolean;
  isDeleting?: boolean;
  isReauthorizing?: boolean;
}

export interface ProviderCardProps {
  provider: IdentityProvider;
  onEdit: (providerId: string) => void;
  onDelete: (providerId: string) => void;
  onConnect: (providerId: string) => void;
}

export interface TemplateCardProps {
  template: ProviderTemplate;
  onSelect: (templateId: string) => void;
}
